import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import {
  provideRouter,
  withPreloading,
  PreloadAllModules,
  Router,
  NavigationEnd
} from '@angular/router';
import { provideHttpClient } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideIonicAngular } from '@ionic/angular/standalone';

import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { AuthEffects } from './app/store/auth/auth.effects';
import { authReducer } from './app/store/auth/auth.reducer';
import { ParticipationsEffects } from './app/store/participations/participations.effect';
import { participationsReducer } from './app/store/participations/participations.reducer';

import { APP_INITIALIZER, isDevMode } from '@angular/core';
import { routes } from './app/app.routes';
import { Store } from '@ngrx/store';
import { AuthService } from './app/services/auth.service';
import { loginSuccess } from './app/store/auth/auth.actions';
import { UserService } from './app/services/user.service';

import { provideServiceWorker } from '@angular/service-worker';

// 🔥 Firebase
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { provideFirestore, getFirestore } from '@angular/fire/firestore';
import { provideStorage, getStorage } from '@angular/fire/storage';

// ✅ Analytics provider (AngularFire)
import { provideAnalytics } from '@angular/fire/analytics';
// ✅ Analytics SDK (Firebase)
import { getAnalytics, logEvent } from 'firebase/analytics';

// ⬇️ Functions con región
import { provideFunctions, getFunctions } from '@angular/fire/functions';

import { environment } from './environments/environment';

// ⬇️ Reset de estado local por versión
import { initLocalStateVersion } from './app/core/local-state-version';

// RxJS
import { filter } from 'rxjs';

/**
 * 🔁 Reset fuerte de Service Workers + caches SOLO una vez en esta versión
 * para evitar comportamientos distintos entre challup.app y challup-67cdd.web.app
 *
 * ✅ FIX:
 * - esperamos a que termine
 * - marcamos flag SOLO al final
 * - si ha reseteado, forzamos reload 1 vez (importantísimo)
 *
 * ✅ COMPAT:
 * - NO usamos Promise.allSettled (tu target/lib no incluye ES2020)
 */
async function hardResetServiceWorkersAndCachesOnce(): Promise<boolean> {
  try {
    const FLAG_KEY = 'challup_sw_reset_2026-01-18'; // ✅ cambia versión para forzar 1 reset nuevo

    if (localStorage.getItem(FLAG_KEY) === '1') {
      return false;
    }

    let didSomething = false;

    // Helper: run tasks ignorando errores (equivalente a allSettled "light")
    const runIgnoreErrors = async (tasks: Array<Promise<any>>) => {
      for (const t of tasks) {
        try {
          await t;
        } catch {
          // ignore
        }
      }
    };

    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations().catch(() => []);
      if (regs && regs.length) {
        didSomething = true;
        await runIgnoreErrors(regs.map((r) => r.unregister()));
      }
    }

    if (window.caches) {
      const keys = await caches.keys().catch(() => []);
      if (keys && keys.length) {
        didSomething = true;
        await runIgnoreErrors(keys.map((k) => caches.delete(k)));
      }
    }

    // ✅ marca flag al final
    localStorage.setItem(FLAG_KEY, '1');

    return didSomething;
  } catch {
    return false;
  }
}

/** 🔧 Inicializador de autenticación */
export function initializeAuthFactory(
  authService: AuthService,
  userService: UserService,
  store: Store
) {
  return () => {
    if (environment.useMockAuth) {
      console.warn('[Auth] Modo MOCK activado: se usa usuario local');

      const fakeUser = {
        uid: 'dev123',
        email: 'dev@local.test',
        username: 'devuser',
        photoURL: ''
      };

      store.dispatch(loginSuccess({ user: fakeUser }));
      return Promise.resolve();
    }

    // 🚀 Firebase Auth real (login se maneja desde AuthService)
    return Promise.resolve();
  };
}

/**
 * ✅ Normaliza URLs para que GA4 no te cree una “página” distinta por cada :uid o :day
 */
function normalizeChallupPath(urlAfterRedirects: string): string {
  const path = (urlAfterRedirects || '').split('?')[0].split('#')[0];

  // Tabs
  if (path.startsWith('/tabs/feed/')) return '/tabs/feed/:day';
  if (path.startsWith('/tabs/explore/')) return '/tabs/explore/:day';
  if (path.startsWith('/tabs/user/')) return '/tabs/user/:uid';

  // Admin
  if (path.startsWith('/admin/users/')) return '/admin/users/:uid';

  return path || '/';
}

/**
 * ✅ Inicializador de Analytics (SPA): page_view en cada NavigationEnd
 */
export function initializeAnalyticsRoutingFactory(router: Router) {
  return () => {
    if (typeof window === 'undefined') return;

    try {
      const analytics = getAnalytics();

      router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe((e) => {
          const page_path = normalizeChallupPath(e.urlAfterRedirects);

          logEvent(analytics, 'page_view', {
            page_path,
            page_location: window.location.href,
            page_title: page_path
            // debug_mode: true  // ✅ si quieres, solo en dev; ahora lo dejamos limpio
          });
        });
    } catch (err) {
      console.warn('[Analytics] No se pudo inicializar o enviar page_view', err);
    }
  };
}

async function start() {
  // 1) Reset fuerte SW/caches (una vez por versión)
  const didReset = await hardResetServiceWorkersAndCachesOnce();

  // ✅ Si reseteamos, recargamos YA para evitar quedar “a medias”
  if (didReset) {
    window.location.reload();
    return;
  }

  // 2) Reset de estado local por versión
  initLocalStateVersion();

  // 3) Bootstrap Angular
  await bootstrapApplication(AppComponent, {
    providers: [
      provideIonicAngular(),
      provideHttpClient(),
      provideAnimations(),

      // 🧭 Rutas y precarga
      provideRouter(routes, withPreloading(PreloadAllModules)),

      // 🧱 NGRX Store y efectos
      provideStore({
        auth: authReducer,
        participations: participationsReducer
      }),
      provideEffects([AuthEffects, ParticipationsEffects]),

      // 🔥 Firebase Providers (✅ primero Firebase App)
      provideFirebaseApp(() => initializeApp(environment.firebase)),
      provideAuth(() => getAuth()),
      provideFirestore(() => getFirestore()),
      provideStorage(() => getStorage()),
      provideFunctions(() => getFunctions(undefined, 'europe-southwest1')),

      // ✅ Analytics (después de Firebase App)
      provideAnalytics(() => getAnalytics()),
      {
        provide: APP_INITIALIZER,
        useFactory: initializeAnalyticsRoutingFactory,
        deps: [Router],
        multi: true
      },

      // 🧩 Inicialización personalizada (mock o real)
      {
        provide: APP_INITIALIZER,
        useFactory: initializeAuthFactory,
        deps: [AuthService, UserService, Store],
        multi: true
      },

      // 🔧 Service Worker (solo en producción)
      provideServiceWorker('ngsw-worker.js', {
        enabled: !isDevMode(),
        registrationStrategy: 'registerWhenStable:30000'
      })
    ]
  });
}

start().catch((e) => {
  console.error('[Bootstrap] fatal error', e);
});
